<template>
  <div class="video-player-page">
    <!-- Header: centered back button + title -->
    <header class="header">
      <h1 class="title">{{ displayTitle }}</h1>
      <button class="back-btn" @click="goBack" aria-label="Go back">← Back</button>
    </header>

    <!-- Player Card -->
    <div class="player-card">
      <div class="video-wrap">
        <video
          ref="video"
          class="video"
          :poster="poster"
          :preload="preload"
          :src="resolvedSrc"
          controls
          playsinline
          @loadedmetadata="onLoadedMetadata"
          @timeupdate="onTimeUpdate"
          @volumechange="onVolumeChange"
          @ratechange="onRateChange"
          @error="onError"
          @play="onPlay"
          @pause="onPause"
        >
          Your browser does not support the video tag.
        </video>

        <!-- Center play/pause overlay (like YouTube) -->
        <div class="center-play-overlay">
          <button
            type="button"
            class="center-play-btn"
            :class="{ 'center-play-btn--hidden': isPlaying }"
            @click.stop="togglePlay"
            :aria-label="isPlaying ? 'Pause video' : 'Play video'"
          >
            <span v-if="!isPlaying">▶</span>
          </button>
        </div>

        <!-- Error message -->
        <div v-if="loadError" class="error-banner">
          ⚠️ Oops! We couldn't load the video: {{ resolvedSrc }}
          <div class="error-tip">
            Place your file in /public/videos or update <code>src</code> passed to the component.
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "VideoPlayerView",
  props: {
    /**
     * Video source path, e.g. "/videos/vowels-song.mp4"
     * Will be resolved from /public in a Vue CLI/Vite app.
     */
    src: {
      type: String,
      default: "/videos/vowels-song.mp4",
    },
    /**
     * Optional display title.
     * If empty, a title will be generated from the file name.
     */
    title: {
      type: String,
      default: "",
    },
    /**
     * Poster image path, e.g. "/images/vocably-poster.jpg".
     */
    poster: {
      type: String,
      default: "/images/vocably-poster.jpg",
    },
    /**
     * Video preload behavior.
     */
    preload: {
      type: String,
      default: "metadata",
      validator: (value) => ["none", "metadata", "auto"].includes(value),
    },
  },
  data() {
    return {
      duration: 0,
      currentTime: 0,
      volume: 1,
      playbackRate: 1,

      loadError: false,
      lastSaveKey: "",
      lastSavedSecond: null,

      isPlaying: false,
    };
  },
  computed: {
    resolvedSrc() {
      // For Vue apps, absolute path points to /public
      return this.src || "/videos/the-vowel-adventure.mp4";
    },
    displayTitle() {
      // Prefer explicit title prop
      const explicitTitle = (this.title || "").trim();
      if (explicitTitle) return explicitTitle;

      // Fallback: derive from the file name
      const src = this.resolvedSrc || "";
      const fileName = src.split("/").pop() || "";
      const withoutExt = fileName.replace(/\.[^/.]+$/, "");
      const withSpaces = withoutExt.replace(/[-_]+/g, " ").trim();
      if (!withSpaces) return "Vocably Player";

      return withSpaces
        .toLowerCase()
        .split(" ")
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    },
  },
  mounted() {
    // Key used to persist state per video
    this.lastSaveKey = `player:${this.resolvedSrc}`;

    // Restore saved settings (safe parse)
    let saved = {};
    try {
      const raw = localStorage.getItem(this.lastSaveKey);
      if (raw) saved = JSON.parse(raw);
    } catch {
      saved = {};
    }

    if (saved.volume != null) this.volume = saved.volume;
    if (saved.playbackRate != null) this.playbackRate = saved.playbackRate;
    if (saved.currentTime != null) this.currentTime = saved.currentTime;

    const v = this.$refs.video;
    if (v) {
      // Apply restored settings to the video element
      v.volume = this.volume;
      v.playbackRate = this.playbackRate;
      // currentTime is applied once metadata is loaded (see onLoadedMetadata)
    }
  },
  beforeUnmount() {
    this.saveState();
  },
  methods: {
    goBack() {
      if (this.$router && this.$router.back) this.$router.back();
      else window.history.back();
    },
    onLoadedMetadata(e) {
      this.loadError = false;
      this.duration = e.target.duration || 0;

      // Resume from last position if it’s within the duration
      if (this.currentTime > 0 && this.currentTime < this.duration) {
        e.target.currentTime = this.currentTime;
      }
    },
    onTimeUpdate(e) {
      const v = e.target;
      this.currentTime = v.currentTime;
      this.duration = v.duration || this.duration;

      // Persist at most once per 5-second boundary
      if (this.lastSaveKey) {
        const currentSecond = Math.floor(this.currentTime);
        if (
          currentSecond % 5 === 0 &&
          currentSecond !== this.lastSavedSecond
        ) {
          this.lastSavedSecond = currentSecond;
          this.saveState();
        }
      }
    },
    onVolumeChange(e) {
      const v = e.target;
      this.volume = v.volume;
      this.saveState();
    },
    onRateChange(e) {
      const v = e.target;
      this.playbackRate = v.playbackRate;
      this.saveState();
    },
    onError() {
      this.loadError = true;
    },
    onPlay() {
      this.isPlaying = true;
    },
    onPause() {
      // Fires on explicit pause and when the video ends
      this.isPlaying = false;
    },
    togglePlay() {
      const v = this.$refs.video;
      if (!v) return;

      if (v.paused || v.ended) {
        v.play();
      } else {
        v.pause();
      }
    },
    saveState() {
      if (!this.lastSaveKey) return;

      const state = {
        currentTime: this.currentTime,
        volume: this.volume,
        playbackRate: this.playbackRate,
      };

      try {
        localStorage.setItem(this.lastSaveKey, JSON.stringify(state));
      } catch {
        // Ignore errors (e.g., private mode / quota exceeded)
      }
    },
  },
};
</script>

<style scoped>
/* Make sizing predictable and avoid layout overflow */
*,
*::before,
*::after {
  box-sizing: border-box;
}

.video-player-page {
  position: relative;
  width: 100%;
  max-width: 100vw;
  min-height: 100vh;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
}

/* Header: centered back button + title */
.header {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px 16px 8px;
  background: #f8fafc;
  border-bottom: 1px solid #e5e7eb;
  width: 100%;
  max-width: 100%;
}

.back-btn {
  border: none;
  background: #ffffff;
  color: #1f2937;
  font-weight: 700;
  padding: 8px 12px;
  border-radius: 10px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.06);
  cursor: pointer;
  margin: 8px;
  max-width: 100%;
}
.back-btn:hover {
  transform: translateY(-1px);
}

.title {
  font-size: 1.1rem;
  font-weight: 800;
  color: #1f2937;
  margin: 0;
  text-align: center;
  word-break: break-word;
}

/* Player card */
.player-card {
  width: 100%;
  max-width: 800px;
  margin: 16px auto 24px;
  background-color: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 14px;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.06);
}

/* Video wrap with 16:9 ratio */
.video-wrap {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  background: #0b1020;
  border-radius: 12px;
  overflow: hidden;
}

.video {
  display: block;
  width: 100%;
  height: 100%;
  max-width: 100%;
  object-fit: contain;
  background: #000;
}

/* Center play/pause overlay */
.center-play-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none; /* let only the button catch clicks */
}

.center-play-btn {
  pointer-events: auto;
  width: 72px;
  height: 72px;
  border-radius: 50%;
  border: none;
  background: rgba(0, 0, 0, 0.7);
  color: #ffffff;
  font-size: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.4);
  transition: transform 0.15s ease, opacity 0.15s ease, background-color 0.15s ease;
}
.center-play-btn:hover {
  transform: scale(1.05);
}

/* Invisible but still clickable while playing */
.center-play-btn--hidden {
  background-color: transparent;
  box-shadow: none;
  opacity: 0;
}

/* Error banner */
.error-banner {
  position: absolute;
  left: 12px;
  top: 12px;
  right: 12px;
  background: #fff1f2;
  color: #9f1239;
  border: 1px solid #fecdd3;
  border-radius: 10px;
  padding: 8px 10px;
  font-weight: 700;
  font-size: 0.9rem;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  max-width: calc(100% - 24px);
}
.error-tip {
  font-weight: 600;
  font-size: 0.8rem;
  color: #be123c;
  margin-top: 4px;
}

/* Small screens */
@media (max-width: 640px) {
  .header {
    padding: 12px 12px 6px;
  }

  .title {
    font-size: 1rem;
  }

  .player-card {
    margin: 12px auto 20px;
    padding: 10px;
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.05);
  }

  .center-play-btn {
    width: 56px;
    height: 56px;
    font-size: 26px;
  }
}
</style>